import { TEST_STATUS, type OverallTestStatus } from '../types';

export type TestStatus = OverallTestStatus;

type CalibrationResults = {
  completedPoints: number;
  totalPoints: number;
  asFoundPassed: boolean;
  asLeftPassed: boolean;
  asFoundStatus?: TestStatus;
  asLeftStatus?: TestStatus;
};

type StatusConfig = {
  label: string;
  color: 'success' | 'error' | 'warning' | 'default';
  bgColor: string;
  borderColor?: string;
  textColor?: string;
};

export const calculateTestStatus = (
  totalPoints: number,
  completedCount: number,
  passed: boolean
): TestStatus => {
  if (completedCount === 0) {
    return TEST_STATUS.NOT_STARTED;
  }
  if (completedCount < totalPoints) {
    return TEST_STATUS.IN_PROGRESS;
  }
  return passed ? TEST_STATUS.PASS : TEST_STATUS.FAIL;
};

export const getStatusConfig = (
  isDarkMode: boolean
): Record<TestStatus, Omit<StatusConfig, 'label'>> => ({
  [TEST_STATUS.PASS]: {
    color: 'success',
    bgColor: isDarkMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(76, 175, 80, 0.25)',
    borderColor: undefined,
    textColor: isDarkMode ? 'rgba(52, 211, 153, 1)' : undefined
  },
  [TEST_STATUS.FAIL]: {
    color: 'error',
    bgColor: isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(244, 67, 54, 0.25)',
    borderColor: undefined,
    textColor: isDarkMode ? 'rgba(248, 113, 113, 1)' : undefined
  },
  [TEST_STATUS.IN_PROGRESS]: {
    color: 'warning',
    bgColor: isDarkMode ? 'rgba(245, 158, 11, 0.3)' : 'rgb(255, 242, 224)',
    borderColor: undefined,
    textColor: isDarkMode ? 'rgba(251, 191, 36, 1)' : undefined
  },
  [TEST_STATUS.NOT_STARTED]: {
    color: 'default',
    bgColor: isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgb(255, 242, 224)',
    borderColor: undefined,
    textColor: isDarkMode ? 'rgba(156, 163, 175, 1)' : undefined
  }
});

export const getCalibrationStatus = (
  results: CalibrationResults,
  t: (key: string) => string,
  isDarkMode: boolean = false
): StatusConfig => {
  const STATUS_CONFIG = getStatusConfig(isDarkMode);

  // Use the individual column statuses if available
  if (results.asFoundStatus && results.asLeftStatus) {
    // If both columns are not started
    if (
      results.asFoundStatus === TEST_STATUS.NOT_STARTED &&
      results.asLeftStatus === TEST_STATUS.NOT_STARTED
    ) {
      return {
        ...STATUS_CONFIG[TEST_STATUS.NOT_STARTED],
        label: t('testGrid.notStarted')
      };
    }

    // If either column is in progress or not started (but not both not started)
    if (
      results.asFoundStatus === TEST_STATUS.IN_PROGRESS ||
      results.asLeftStatus === TEST_STATUS.IN_PROGRESS ||
      results.asFoundStatus === TEST_STATUS.NOT_STARTED ||
      results.asLeftStatus === TEST_STATUS.NOT_STARTED
    ) {
      return {
        ...STATUS_CONFIG[TEST_STATUS.IN_PROGRESS],
        label: t('testGrid.inProgress')
      };
    }

    // Both columns are complete (pass or fail)
    // Overall passes only if As Left passes
    const isPassed = results.asLeftStatus === TEST_STATUS.PASS;
    const status = isPassed ? TEST_STATUS.PASS : TEST_STATUS.FAIL;
    const statusLabel = isPassed ? t('testGrid.pass') : t('testGrid.fail');

    return {
      ...STATUS_CONFIG[status],
      label: statusLabel
    };
  }

  // Fallback to old logic if column statuses not provided
  if (results.completedPoints === 0) {
    return {
      ...STATUS_CONFIG[TEST_STATUS.NOT_STARTED],
      label: t('testGrid.notStarted')
    };
  }

  if (results.completedPoints < results.totalPoints) {
    return {
      ...STATUS_CONFIG[TEST_STATUS.IN_PROGRESS],
      label: t('testGrid.inProgress')
    };
  }

  const isPassed = results.asLeftPassed;
  const status = isPassed ? TEST_STATUS.PASS : TEST_STATUS.FAIL;
  const statusLabel = isPassed ? t('testGrid.pass') : t('testGrid.fail');

  return {
    ...STATUS_CONFIG[status],
    label: statusLabel
  };
};
